/**
 * `staffRoleLibrary` — per-org user-extensible roster of position
 * titles (Carpenter, Site Engineer, etc.). Merged with the built-in
 * `DEFAULT_STAFF_ROLES` preset at the form layer.
 *
 * Mirror of `src/features/staff/staffRoleLibrary.ts` on mobile —
 * same Firestore path, same doc-id strategy (`${orgId}_${key}`).
 * Doc-id strategy gives idempotent create (set-with-merge) so the
 * same role can't be added twice.
 *
 * Permission: read = any org member; write = `canOrgFinance`
 * (SA / Admin / Manager / Accountant) per firestore.rules.
 */
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';

// ───── Built-in roster ────────────────────────────────────
//
// `StaffRoleOption`, `DEFAULT_STAFF_ROLES`, and `toRoleKey` now
// live in `@shared/perms/staff` so mobile + web share one copy.
// Imported here for local use AND re-exported so existing
// imports from this module keep working.
import {
  DEFAULT_STAFF_ROLES,
  toRoleKey,
  type StaffRoleOption,
} from '@shared/perms';
export type { StaffRoleOption };
export { DEFAULT_STAFF_ROLES, toRoleKey };

// ───── Doc shape ──────────────────────────────────────────

export type StaffRoleLibraryItem = {
  id: string;
  orgId: string;
  key: string;
  label: string;
  normalizedLabel: string;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
};

// ───── CRUD ────────────────────────────────────────────────

export async function createStaffRole(input: {
  orgId: string;
  label: string;
  createdBy: string;
}): Promise<string> {
  const cleanLabel = input.label.trim();
  if (!cleanLabel) throw new Error('Role label is required.');
  const normalized = cleanLabel.toLowerCase();
  const key = toRoleKey(cleanLabel);
  const id = `${input.orgId}_${key}`;
  await setDoc(
    doc(db, 'staffRoleLibrary', id),
    {
      orgId: input.orgId,
      key,
      label: cleanLabel,
      normalizedLabel: normalized,
      createdBy: input.createdBy,
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
  return id;
}

export async function deleteStaffRole(id: string): Promise<void> {
  await deleteDoc(doc(db, 'staffRoleLibrary', id));
}

// ───── Live read hook ──────────────────────────────────────

export type UseStaffRolesResult = {
  /** Merged: defaults + the org's customs, de-duped by key, sorted by label. */
  data: StaffRoleOption[];
  /** Raw library docs (customs only) so callers can find ids to delete. */
  customs: StaffRoleLibraryItem[];
  loading: boolean;
};

export function useStaffRoles(orgId: string | null | undefined): UseStaffRolesResult {
  const [library, setLibrary] = useState<StaffRoleLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setLibrary([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'staffRoleLibrary'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: StaffRoleLibraryItem[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<StaffRoleLibraryItem, 'id'>),
        }));
        rows.sort((a, b) => a.label.localeCompare(b.label));
        setLibrary(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useStaffRoles] snapshot error:', err);
        setLibrary([]);
        setLoading(false);
      },
    );
  }, [orgId]);

  const data = useMemo(() => {
    const map = new Map<string, StaffRoleOption>();
    for (const item of DEFAULT_STAFF_ROLES) map.set(item.key, item);
    for (const item of library) map.set(item.key, { key: item.key, label: item.label });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [library]);

  return { data, customs: library, loading };
}
